/**
 * 🔐 usePermissions Hook
 * Easy permission checking in React components
 */

import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  hasRole,
  isSuperAdmin,
  getResourcePermissions,
  canAccessFeature,
  UserPermissions,
  Permissions,
  FeaturePermissions,
} from '@/utils/permissions';

export const usePermissions = () => {
  // Get current user from Redux store
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Build user permissions object
  // The backend /admin/profile returns: { role: "admin", permissions: ["corporate:view", ...] }
  // The role field can be either a string or an object depending on the endpoint
  const userPermissions: UserPermissions | null = currentUser?.role
    ? {
        role: {
          id: (currentUser.role as any).id || '',
          // Handle both string role and object role
          name: typeof currentUser.role === 'string'
            ? currentUser.role
            : (currentUser.role as any).name || currentUser.role || 'User',
          type: ((currentUser.role as any).type as 'system' | 'custom') || 'system',
          // Priority: role.permissions (if role is object) OR user.permissions (from profile endpoint)
          permissions: (currentUser.role as any).permissions || (currentUser as any).permissions || [],
        },
        customPermissions: (currentUser as any).customPermissions,
      }
    : null;

  // Debug logging (can be removed in production)
  if (typeof window !== 'undefined' && currentUser && import.meta.env.MODE === 'development') {
    // Log permissions for debugging
    const permCount = userPermissions?.role?.permissions?.length || 0;
    if (permCount > 0) {
      console.log('🔐 [usePermissions] Loaded', permCount, 'permissions for role:', userPermissions?.role?.name);
    } else {
      console.warn('⚠️ [usePermissions] No permissions found for user:', currentUser.email);
      console.warn('   Role:', currentUser.role);
      console.warn('   Permissions:', (currentUser as any).permissions);
    }
  }

  return {
    // Current user and role
    currentUser,
    userPermissions,
    role: currentUser?.role?.name || currentUser?.role || null,

    // Permission checking functions
    can: (resource: string, action: string) => hasPermission(userPermissions, resource, action),
    canAny: (checks: Array<{ resource: string; action: string }>) =>
      hasAnyPermission(userPermissions, checks),
    canAll: (checks: Array<{ resource: string; action: string }>) =>
      hasAllPermissions(userPermissions, checks),

    // Role checking
    hasRole: (roleName: string) => hasRole(userPermissions, roleName),
    isSuperAdmin: () => {
      // Simple check: role string is 'super_admin' or 'super admin'
      const roleStr = typeof currentUser?.role === 'string'
        ? currentUser.role
        : currentUser?.role?.name || '';

      const isSuperAdminRole = roleStr.toLowerCase().replace(/[_\s]/g, '') === 'superadmin';

      return isSuperAdminRole || isSuperAdmin(userPermissions);
    },

    // Resource permissions
    getResourcePermissions: (resource: string) => getResourcePermissions(userPermissions, resource),

    // Feature access
    canAccessFeature: (feature: keyof typeof FeaturePermissions) =>
      canAccessFeature(userPermissions, feature),

    // Common permission constants
    Permissions,
    FeaturePermissions,
  };
};

/**
 * Hook specifically for permission-based rendering
 */
export const usePermissionGuard = (resource: string, action: string) => {
  const { can } = usePermissions();
  return can(resource, action);
};

/**
 * Hook for feature-based access control
 */
export const useFeatureAccess = (feature: keyof typeof FeaturePermissions) => {
  const { canAccessFeature } = usePermissions();
  return canAccessFeature(feature);
};
